import {useEffect, useState, useCallback} from 'react';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';

export const SCROLL_THRESHOLD_PX = 200;

interface UseScrollToBottomOptions {
  containerId: string;
  threshold?: number;
}

interface UseScrollToBottomReturn {
  showScrollButton: boolean;
  scrollToBottom: () => void;
}

function calculateDistanceFromBottom(container: HTMLElement): number {
  const {scrollTop, scrollHeight, clientHeight} = container;
  return scrollHeight - scrollTop - clientHeight;
}

function attachScrollListener(container: HTMLElement, handler: () => void): void {
  container.addEventListener('scroll', handler, {passive: true});
}

function detachScrollListener(container: HTMLElement, handler: () => void): void {
  container.removeEventListener('scroll', handler);
}

function observeContainerChanges(parent: HTMLElement, callback: MutationCallback): MutationObserver {
  const observer = new MutationObserver(callback);
  observer.observe(parent, {childList: true, subtree: true});
  return observer;
}

export function useScrollToBottom({containerId, threshold = SCROLL_THRESHOLD_PX}: UseScrollToBottomOptions): UseScrollToBottomReturn {
  const {state: aiChatState} = useAiChat();
  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollToBottom = useCallback((): void => {
    const chatContainer = document.getElementById(containerId);

    if (!chatContainer) {
      return;
    }

    chatContainer.scrollTo({
      top: chatContainer.scrollHeight,
      behavior: 'smooth',
    });
  }, [containerId]);

  useEffect(() => {
    if (!aiChatState.currentChatId) {
      setShowScrollButton(false);
      return;
    }

    let chatContainer = document.getElementById(containerId);

    if (!chatContainer) {
      return;
    }

    const updateScrollButtonVisibility = (): void => {
      const container = document.getElementById(containerId);
      if (!container) {
        return;
      }

      const distanceFromBottom = calculateDistanceFromBottom(container);
      setShowScrollButton(distanceFromBottom > threshold);
    };

    const handleContainerReplacement = (): void => {
      const newContainer = document.getElementById(containerId);
      if (newContainer && newContainer !== chatContainer) {
        if (chatContainer) {
          detachScrollListener(chatContainer, updateScrollButtonVisibility);
        }

        chatContainer = newContainer;
        attachScrollListener(chatContainer, updateScrollButtonVisibility);
        updateScrollButtonVisibility();
      }
    };

    attachScrollListener(chatContainer, updateScrollButtonVisibility);
    setShowScrollButton(false);
    updateScrollButtonVisibility();

    const parent = chatContainer.parentElement;
    const observer = parent ? observeContainerChanges(parent, handleContainerReplacement) : null;

    return (): void => {
      observer?.disconnect();
      if (chatContainer) {
        detachScrollListener(chatContainer, updateScrollButtonVisibility);
      }
    };
  }, [containerId, threshold, aiChatState.currentChatId]);

  return {
    showScrollButton,
    scrollToBottom,
  };
}
